import { useState } from 'react';

import {
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  LogIn,
} from 'lucide-react';

import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';

interface LoginFormProps {
  onSubmit: (
    email: string,
    password: string
  ) => void;
  onForgotPassword: () => void;
  isSubmitting: boolean;
}

export default function LoginForm({
  onSubmit,
  onForgotPassword,
  isSubmitting,
}: LoginFormProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] =
    useState(false);

  const handleSubmit = (
    e: React.FormEvent
  ) => {
    e.preventDefault();

    onSubmit(email, password);
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="mt-5 space-y-5"
    >
      {/* Email */}
      <div className="space-y-2">
        <Label htmlFor="email">
          Email
        </Label>

        <Input
          id="email"
          type="email"
          placeholder="you@company.com"
          value={email}
          onChange={(e) =>
            setEmail(e.target.value)
          }
          required
          disabled={isSubmitting}
        />
      </div>

      {/* Password */}
      <div className="space-y-2">
        <Label htmlFor="password">
          Password
        </Label>

        <div className="relative">
          <Input
            id="password"
            type={
              showPassword
                ? 'text'
                : 'password'
            }
            placeholder="Enter your password"
            value={password}
            onChange={(e) =>
              setPassword(e.target.value)
            }
            required
            disabled={isSubmitting}
            className="pr-10"
          />

          <button
            type="button"
            onClick={() =>
              setShowPassword(
                (current) => !current
              )
            }
            disabled={isSubmitting}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-50"
            tabIndex={-1}
            aria-label={
              showPassword
                ? 'Hide password'
                : 'Show password'
            }
          >
            {showPassword ? (
              <EyeOff className="h-4 w-4" />
            ) : (
              <Eye className="h-4 w-4" />
            )}
          </button>
        </div>

        {/* Forgot Password */}
        <div className="flex justify-end pt-0.5">
          <button
            type="button"
            onClick={onForgotPassword}
            disabled={isSubmitting}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary transition-colors hover:text-primary/80 hover:underline disabled:pointer-events-none disabled:opacity-50"
          >
            <KeyRound className="h-3.5 w-3.5" />
            Forgot Password?
          </button>
        </div>
      </div>

      {/* Sign In Button */}
      <Button
        type="submit"
        className="h-11 w-full"
        disabled={isSubmitting}
      >
        {isSubmitting ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Signing in...
          </>
        ) : (
          <>
            <LogIn className="mr-2 h-4 w-4" />
            Sign In
          </>
        )}
      </Button>
    </form>
  );
}